'use client';

import { useLocale } from 'next-intl';

import type { ButtonVariant } from '@commons/components/Button';
import RichText from '@commons/components/RichText';
import { isExternalURL } from '@commons/helpers/url';
import { redirect } from '@commons/i18n/navigation';
import PageBuilder from '@commons/pagebuilder';
import WebformDrawer from '@commons/pagebuilder/webforms/Drawer';
import type { DrupalBlock, DrupalLink } from '@commons/types/drupal';

// Type definition for the Webform component properties.

type Props = DrupalBlock<{
  id: string;
  submitLabel?: string;
  inDrawer?: boolean;
  drawerTriggerlabel?: string;
  drawerTriggerVariant?: ButtonVariant | null;
  drawerTriggerXl?: boolean;
  drawerHeader?: string;
  thankYou?: string;
  redirection?: DrupalLink;
}>;

/**
 * Webform inline block component.
 *
 * Renders the appropriate form based on the webform id.
 */

const WebformBlock = ({ content }: Props) => {
  const locale = useLocale();

  if (!content?.id) return null;

  const {
    id,
    submitLabel,
    inDrawer,
    drawerTriggerlabel,
    drawerTriggerVariant,
    drawerTriggerXl,
    drawerHeader,
    thankYou,
    redirection,
  } = content;

  const renderedDrawerHeader = drawerHeader ? (
    <RichText value={drawerHeader} marginBottom="lg" />
  ) : undefined;

  const renderedThankYou = thankYou ? <RichText value={thankYou} /> : undefined;

  let handleOnSuccess: (() => void) | undefined;

  if (redirection?.url) {
    handleOnSuccess = () => {
      setTimeout(() => {
        if (isExternalURL(redirection.url)) {
          window.location.href = redirection.url;
        } else {
          redirect({ href: redirection.url, locale });
        }
      }, 3000);
    };
  }

  const renderedWebform = PageBuilder.renderWebform(id, {
    submitLabel,
    inDrawer,
    drawerHeader: renderedDrawerHeader,
    thankYou: renderedThankYou,
    onSuccess: handleOnSuccess,
  });

  if (inDrawer && drawerTriggerlabel) {
    return (
      <WebformDrawer
        webformId={id}
        triggerProps={{
          label: drawerTriggerlabel,
          variant: drawerTriggerVariant ?? 'secondary',
          xl: drawerTriggerXl,
        }}
      >
        {renderedWebform}
      </WebformDrawer>
    );
  }

  return renderedWebform;
};

export default WebformBlock;
